/**
 * Build a print-ready HTML document for a form template.
 *
 * Two modes:
 *   - **Filled** — pass `fieldData` to render a member's submitted answers.
 *     Selected options are highlighted; selects/radios still render as
 *     visible option lists so the printout shows the universe of choices.
 *   - **Blank hard-copy** — pass `mode: 'hard-copy-blank'` (and omit fieldData)
 *     to render an empty form designed for hand-fill. Per spec, dropdowns,
 *     radios, and checkboxes all render as checkbox lists so a person can
 *     tick choices in pen and the document can later be uploaded/scanned.
 *
 * Options are normalized via fieldOptions.ts to tolerate the two shapes
 * (string[] and {label, value}[]) that coexist in production form templates.
 */

import type { FormTemplate, FormFieldSchema } from '@/services/api/forms';
import { isSignatureImage, readSignatureRecord } from './signatureFields';
import { normalizeOptions } from './fieldOptions';

export type PrintMode = 'filled' | 'hard-copy-blank';

export interface BuildPrintHtmlArgs {
  template: FormTemplate;
  mode?: PrintMode;
  fieldData?: Record<string, any>;
  trackingNumber?: string | null;
  memberName?: string | null;
  status?: string | null;
}

export const escapeHtml = (s: unknown): string => {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const fieldLabel = (field: FormFieldSchema) =>
  `${escapeHtml(field.label)}${field.required ? ' <span style="color:#dc2626">*</span>' : ''}`;

export const checkboxRow = (label: string, checked: boolean) => `
  <div style="display:flex;align-items:center;gap:6px;padding:2px 0;font-size:13px">
    <span style="width:14px;height:14px;border:1.5px solid #333;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;flex-shrink:0">${checked ? '✓' : '&nbsp;'}</span>
    <span style="font-weight:${checked ? '600' : '400'}">${escapeHtml(label)}</span>
  </div>
`;

const radioCircleRow = (label: string, selected: boolean) => `
  <div style="display:flex;align-items:center;gap:6px;padding:2px 0;font-size:13px">
    <span style="width:12px;height:12px;border:1.5px solid #333;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:9px;flex-shrink:0">${selected ? '●' : '&nbsp;'}</span>
    <span style="font-weight:${selected ? '600' : '400'}">${escapeHtml(label)}</span>
  </div>
`;

export const fieldBlock = (labelHtml: string, body: string, hint?: string) => `
  <div style="margin-bottom:14px">
    <label style="font-weight:600;font-size:13px;color:#374151">${labelHtml}</label>
    ${hint ? `<div style="font-size:11px;color:#6b7280;margin-top:2px">${escapeHtml(hint)}</div>` : ''}
    <div style="margin-top:4px">${body}</div>
  </div>
`;

export const writingLine = (height = 22) => `
  <div style="border-bottom:1px solid #333;height:${height}px;min-height:${height}px"></div>
`;

export const writingBox = (height = 60) => `
  <div style="border:1px solid #333;border-radius:4px;height:${height}px;min-height:${height}px"></div>
`;

const isEmptyValue = (v: unknown): boolean =>
  v == null || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * `emptyAsWriteIn` (print packet, TRUS-636): in filled mode, a field with no
 * answer prints exactly as it would on a blank hard copy — a line, box or
 * unticked options — so staff can collect the missing answer on paper.
 */
const renderFieldHtml = (
  field: FormFieldSchema,
  mode: PrintMode,
  fieldData?: Record<string, any>,
  emptyAsWriteIn = false,
): string => {
  const labelHtml = fieldLabel(field);
  const value = fieldData?.[field.field_id];
  const blank = mode === 'hard-copy-blank' || (emptyAsWriteIn && isEmptyValue(value));
  const opts = normalizeOptions(field.options);

  if (field.field_type === 'section_header') {
    return `
      <div style="margin:14px 0 6px">
        <h4 style="margin:0;font-size:14px;font-weight:600;color:#111827">${escapeHtml(field.label)}</h4>
        ${field.description ? `<p style="margin:2px 0 0;font-size:11px;color:#6b7280">${escapeHtml(field.description)}</p>` : ''}
      </div>
    `;
  }

  if (field.field_type === 'divider') {
    return '<hr style="border:none;border-top:1px solid #d1d5db;margin:8px 0" />';
  }

  if (field.field_type === 'spacer') {
    return '<div style="height:8px"></div>';
  }

  // Select — always rendered as a checkbox list. In hard-copy blank mode all
  // are unchecked; in filled mode the matching option is marked.
  if (field.field_type === 'select' && opts.length) {
    const hint = blank ? 'Check one' : undefined;
    const rows = opts.map((o) => checkboxRow(o.label, !blank && value === o.value)).join('');
    return fieldBlock(labelHtml, rows, hint);
  }

  // Radio — in hard-copy mode the user requested radios convert to checkboxes
  // too (for unambiguous pen-and-paper marking). In filled mode preserve the
  // visual distinction with a filled circle.
  if (field.field_type === 'radio' && opts.length) {
    const hint = blank ? 'Check one' : undefined;
    const rows = opts
      .map((o) =>
        blank ? checkboxRow(o.label, false) : radioCircleRow(o.label, value === o.value)
      )
      .join('');
    return fieldBlock(labelHtml, rows, hint);
  }

  // Checkbox with options — multi-select list.
  if (field.field_type === 'checkbox' && opts.length) {
    const hint = blank ? 'Check all that apply' : undefined;
    const checked: string[] = Array.isArray(value) ? value : [];
    const rows = opts
      .map((o) => checkboxRow(o.label, !blank && checked.includes(o.value)))
      .join('');
    return fieldBlock(labelHtml, rows, hint);
  }

  // Single boolean checkbox (no options).
  if (field.field_type === 'checkbox') {
    return fieldBlock(labelHtml, checkboxRow(field.placeholder || 'Yes', !blank && !!value));
  }

  if (field.field_type === 'textarea' || field.field_type === 'rich_text') {
    if (blank) return fieldBlock(labelHtml, writingBox(80));
    const display = value || '';
    return fieldBlock(
      labelHtml,
      `<div style="padding:8px;border:1px solid #d1d5db;border-radius:4px;min-height:40px;font-size:13px;white-space:pre-wrap">${escapeHtml(display)}</div>`
    );
  }

  if (field.field_type === 'signature') {
    // TRUS-763: each signature prints with ITS OWN date. A drawn signature shows
    // the date it was signed; an unsigned one prints a signature line AND a date
    // line, because IFR often prints the plan and collects signatures on paper,
    // and the member and care manager may sign on different days.
    const sigLine = '<div style="border-bottom:1px solid #333;height:40px;width:240px"></div>';
    const dateLine = (filled: string) =>
      `<div style="margin-top:6px;font-size:12px;color:#374151">Date: ${
        filled
          ? escapeHtml(filled)
          : '<span style="display:inline-block;border-bottom:1px solid #333;width:120px;height:14px"></span>'
      }</div>`;
    const record = readSignatureRecord(fieldData, field.field_id);
    if (!blank && isSignatureImage(value)) {
      const signedAt = record?.signed_at ? new Date(record.signed_at) : null;
      const when = signedAt && !Number.isNaN(signedAt.getTime()) ? signedAt.toLocaleDateString() : '';
      const who = record?.signer_name ? `<div style="font-size:12px;color:#374151">${escapeHtml(record.signer_name)}</div>` : '';
      return fieldBlock(
        labelHtml,
        `<img src="${escapeHtml(value)}" style="max-height:60px;border:1px solid #d1d5db;border-radius:4px;padding:4px" />${who}${dateLine(when)}`
      );
    }
    return fieldBlock(labelHtml, `${sigLine}${dateLine('')}`);
  }

  if (field.field_type === 'file') {
    return fieldBlock(labelHtml, blank ? writingBox(30) : `<span style="font-size:12px;color:#6b7280">${value ? `File: ${escapeHtml(value)}` : '—'}</span>`);
  }

  if (field.field_type === 'field_group') {
    return fieldBlock(labelHtml, blank ? writingBox(80) : '<span style="font-size:12px;color:#6b7280">(repeatable group — see live form)</span>');
  }

  // Default scalar field — text, number, email, phone, date, time, datetime.
  if (blank) {
    const tall = field.field_type === 'date' || field.field_type === 'time' || field.field_type === 'datetime' || field.field_type === 'number';
    return fieldBlock(labelHtml, writingLine(tall ? 24 : 24));
  }
  const display = value != null && value !== '' ? escapeHtml(value) : '<span style="color:#9ca3af;font-style:italic">—</span>';
  return fieldBlock(
    labelHtml,
    `<div style="padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;min-height:22px">${display}</div>`
  );
};

/**
 * The section cards of a form, without the page shell. Shared by
 * buildFormPrintHtml and the print packet (TRUS-636), which puts several
 * forms in one document.
 */
export function buildFormSectionsHtml(
  template: Pick<FormTemplate, 'sections'>,
  mode: PrintMode = 'filled',
  fieldData?: Record<string, any>,
  options: { emptyAsWriteIn?: boolean } = {},
): string {
  return (template.sections || [])
    .map((section) => {
      const fieldsHtml = (section.fields || [])
        .map((f) => renderFieldHtml(f, mode, fieldData, options.emptyAsWriteIn))
        .join('');
      return `
        <div style="margin-bottom:20px;border:1px solid #d1d5db;border-radius:8px;page-break-inside:avoid">
          <div style="padding:12px 16px;background:#f9fafb;border-bottom:1px solid #d1d5db">
            <h3 style="margin:0;font-size:15px;font-weight:600;color:#111827">${escapeHtml(section.title || '')}</h3>
            ${section.description ? `<p style="margin:4px 0 0;font-size:12px;color:#6b7280">${escapeHtml(section.description)}</p>` : ''}
          </div>
          <div style="padding:16px">${fieldsHtml}</div>
        </div>
      `;
    })
    .join('');
}

export function buildFormPrintHtml(args: BuildPrintHtmlArgs): string {
  const { template, mode = 'filled', fieldData, trackingNumber, memberName, status } = args;
  const isBlank = mode === 'hard-copy-blank';

  const sectionsHtml = buildFormSectionsHtml(template, mode, fieldData);

  const trackingHeader = trackingNumber
    ? `
      <div style="text-align:center;border-bottom:2px solid #333;padding-bottom:12px;margin-bottom:20px">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666">Document Tracking</div>
        <div style="font-family:monospace;font-size:24px;font-weight:bold;letter-spacing:2px">${escapeHtml(trackingNumber)}</div>
      </div>
    `
    : '';

  const subtitleParts: string[] = [];
  if (memberName) subtitleParts.push(`Member: ${escapeHtml(memberName)}`);
  if (status) subtitleParts.push(`Status: ${escapeHtml(status)}`);
  if (trackingNumber) subtitleParts.push(`Tracking: ${escapeHtml(trackingNumber)}`);
  if (isBlank) subtitleParts.push('Hard-copy for hand-fill');
  const subtitle = subtitleParts.join(' · ');

  const intakeStrip = isBlank
    ? `
      <div style="display:flex;gap:16px;margin-bottom:20px;font-size:12px">
        <div style="flex:1"><div style="color:#6b7280;margin-bottom:2px">Filled by</div>${writingLine()}</div>
        <div style="flex:1"><div style="color:#6b7280;margin-bottom:2px">Date</div>${writingLine()}</div>
        <div style="flex:1"><div style="color:#6b7280;margin-bottom:2px">Member / Contact #</div>${writingLine()}</div>
      </div>
    `
    : '';

  return `<!DOCTYPE html><html><head>
    <title>${escapeHtml(template.name)}</title>
    <style>
      @page { margin: 0.75in; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1a1a1a; line-height: 1.5; margin: 0; padding: 0; }
      * { box-sizing: border-box; }
    </style>
  </head><body>
    ${trackingHeader}
    <h1 style="font-size:20px;margin:0 0 4px">${escapeHtml(template.name)}</h1>
    ${subtitle ? `<p style="font-size:13px;color:#6b7280;margin:0 0 16px">${subtitle}</p>` : ''}
    ${intakeStrip}
    ${sectionsHtml}
    <div style="margin-top:40px;padding-top:16px;border-top:1px solid #d1d5db">
      <div style="display:flex;gap:24px">
        <div style="flex:1">${writingLine(40)}<div style="font-size:11px;color:#888;margin-top:4px">Signature</div></div>
        <div style="width:180px">${writingLine(40)}<div style="font-size:11px;color:#888;margin-top:4px">Date</div></div>
      </div>
    </div>
    ${trackingNumber ? `<div style="text-align:center;margin-top:30px;font-size:10px;color:#aaa">Tracking # ${escapeHtml(trackingNumber)}</div>` : ''}
  </body></html>`;
}

/** Open the printable HTML in a new window and trigger the print dialog. */
export function printInNewWindow(html: string): void {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}
